import { useGetList, useLocaleState, useTranslate } from "ra-core";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

import type { Project, ProjectContactLink, ProjectScore } from "../types";
import { bestContact } from "./contactOrder";
import { scrollToAnchor as goTo } from "./scrollToAnchor";
import { isStaleNews } from "./newsAge";
import { countryName, formatBudget, formatDay } from "./projectUtils";
import { ScoreStamp } from "./ScoreCard";

/** Verdict de la fiche en tête : score, projet en une ligne, contact à joindre en premier, dernière nouvelle. Le
 * détail (critères, preuves, contacts) reste plus bas. Mêmes requêtes que le score et les contacts : lues une fois. */
export const ProjectSummary = ({
  project,
  lastNews,
}: {
  project: Project;
  lastNews: string | null;
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const { data: scores } = useGetList<ProjectScore>("scores", {
    filter: { project_id: project.id, product_id: "ett" },
    pagination: { page: 1, perPage: 1 },
    sort: { field: "id", order: "ASC" },
  });
  const { data: contacts } = useGetList<ProjectContactLink>(
    "project_contact_links",
    {
      filter: { project_id: project.id },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "created_at", order: "ASC" },
    },
  );
  const score = scores?.[0];
  const best = contacts ? bestContact(contacts) : undefined;
  const t = (key: string, options?: object) =>
    translate(`resources.projects.summary.${key}`, options);
  const place = [project.city, countryName(project.country, locale)]
    .filter(Boolean)
    .join(", ");
  // La phase est déjà dans la pastille du titre.
  const what = [formatBudget(project, locale) || t("no_amount"), place]
    .filter(Boolean)
    .join(" · ");
  const name = best
    ? [best.first_name, best.last_name].filter(Boolean).join(" ") ||
      best.company_name
    : null;
  const proof = best?.email_jsonb?.length ? t("email") : t("phone");

  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 border-y border-border py-3 @md:grid-cols-[auto_1fr]">
      <Row label={t("score")}>
        {score ? (
          <button
            type="button"
            onClick={() => goTo("score")}
            className="flex items-center gap-2 rounded-[3px] text-left"
          >
            <span className="text-xl font-bold leading-none tabular-nums">
              {score.score}
            </span>
            <span className="text-xs text-muted-foreground">
              {translate("resources.projects.score.out_of")}
            </span>
            <ScoreStamp />
          </button>
        ) : (
          <span className="text-muted-foreground">
            {translate("resources.projects.score.not_scored")}
          </span>
        )}
      </Row>
      <Row label={t("project")}>{what}</Row>
      <Row label={t("contact")}>
        {best ? (
          <button
            type="button"
            onClick={() => goTo(`contact-${best.contact_id}`)}
            className="rounded-[3px] text-left underline decoration-border underline-offset-2 hover:decoration-foreground"
          >
            <span className="font-medium">{name}</span>
            {best.title && (
              <span className="text-muted-foreground"> · {best.title}</span>
            )}
            <span className="text-muted-foreground"> · {proof}</span>
          </button>
        ) : (
          <span className="text-muted-foreground">
            {t(contacts?.length ? "no_reachable" : "no_contact")}{" "}
            <button
              type="button"
              onClick={() => goTo("contacts")}
              className="rounded-[3px] text-foreground underline underline-offset-2"
            >
              {t("see_contacts")}
            </button>
          </span>
        )}
      </Row>
      {lastNews && (
        <Row label={t("news")}>
          <button
            type="button"
            onClick={() => goTo("sources")}
            className={cn(
              "rounded-[3px] text-left tabular-nums underline decoration-border underline-offset-2 hover:decoration-foreground",
              isStaleNews(lastNews) && "text-inferred-ink",
            )}
          >
            {isStaleNews(lastNews)
              ? t("stale", {
                  date: formatDay(lastNews, locale),
                })
              : formatDay(lastNews, locale)}
          </button>
        </Row>
      )}
    </dl>
  );
};

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="contents">
    <dt className="pt-0.5 text-xs font-semibold text-muted-foreground">
      {label}
    </dt>
    <dd className="min-w-0 text-sm">{children}</dd>
  </div>
);
